import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { amazonOAuthApi } from '../../api/endpoints';
import { FiCheck, FiAlertCircle, FiLoader } from 'react-icons/fi';

/**
 * Page that Amazon redirects to after the user authorizes the app.
 * URL: https://edi.krosno.com/callback?spapi_oauth_code=...&state=...&selling_partner_id=...
 *
 * This page:
 * 1. Extracts query params
 * 2. POSTs them to backend /api/amazon-oauth/exchange
 * 3. Backend exchanges oauth_code for refresh_token and saves to DB
 * 4. Shows success/error and auto-closes the popup window (returns user to authorization page)
 */
const AmazonOAuthCallbackPage: React.FC = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [message, setMessage] = useState<string>('');
  const [accountName, setAccountName] = useState<string>('');

  useEffect(() => {
    const code = params.get('spapi_oauth_code');
    const state = params.get('state');
    const sellingPartnerId = params.get('selling_partner_id') || undefined;
    const errorParam = params.get('error');

    if (errorParam) {
      setStatus('error');
      setMessage(`Amazon returned error: ${errorParam}. ${params.get('error_description') || ''}`);
      return;
    }

    if (!code || !state) {
      setStatus('error');
      setMessage(t('amazonAuthCallback.missingParams'));
      return;
    }

    amazonOAuthApi.exchange(code, state, sellingPartnerId)
      .then((response) => {
        if (response.data.success) {
          setStatus('success');
          setAccountName(response.data.accountName || '');
          setMessage(t('amazonAuthCallback.success'));
          // Auto-close popup after 3s — parent window will detect via polling
          setTimeout(() => {
            try { window.close(); } catch { /* not opened from popup, stay */ }
          }, 3000);
        } else {
          setStatus('error');
          setMessage(response.data.error || t('amazonAuthCallback.unknownError'));
        }
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.response?.data?.error || err.message || t('amazonAuthCallback.unknownError'));
      });
  }, [params, t]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'var(--bg)', padding: '20px',
    }}>
      <div style={{
        maxWidth: '520px', width: '100%', padding: '32px',
        backgroundColor: 'var(--bg-elevated)', borderRadius: '12px',
        border: '1px solid var(--border)', textAlign: 'center',
      }}>
        {status === 'pending' && (
          <>
            <FiLoader className="spin" style={{ fontSize: '48px', color: '#3b82f6', marginBottom: '16px' }} />
            <h2 style={{ marginBottom: '8px' }}>{t('amazonAuthCallback.processing')}</h2>
            <p style={{ color: 'var(--text-muted)' }}>{t('amazonAuthCallback.pleaseWait')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <FiCheck style={{ fontSize: '48px', color: '#10b981', marginBottom: '16px' }} />
            <h2 style={{ marginBottom: '8px', color: '#10b981' }}>{t('amazonAuthCallback.successTitle')}</h2>
            {accountName && <p style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>{accountName}</p>}
            <p style={{ color: 'var(--text-muted)' }}>{message}</p>
            <p style={{ marginTop: '16px', fontSize: '12px', color: 'var(--text-muted)' }}>
              {t('amazonAuthCallback.windowClosing')}
            </p>
          </>
        )}
        {status === 'error' && (
          <>
            <FiAlertCircle style={{ fontSize: '48px', color: '#ef4444', marginBottom: '16px' }} />
            <h2 style={{ marginBottom: '8px', color: '#ef4444' }}>{t('amazonAuthCallback.errorTitle')}</h2>
            <p style={{ color: 'var(--text)', wordBreak: 'break-word' }}>{message}</p>
            <button
              className="btn btn-secondary"
              onClick={() => { try { window.close(); } catch { window.history.back(); } }}
              style={{ marginTop: '16px' }}
            >
              {t('common.close')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AmazonOAuthCallbackPage;
